// src/components/Dialogs/SubmitTemplateDialog.jsx

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
//  IconButton
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useFormContext } from '../../contexts/FormContext';

import CloseIcon from '@mui/icons-material/Close';

// 1) Import our new "open file" picker
import OpenFormPickerDialog from './OpenFormPickerDialog';

// 2) Import your publishTemplate utility (adjust the path if needed)
import { publishTemplate } from '../../utils/publishTemplate';

/**
 * SubmitTemplateDialog
 *
 * Props:
 * - open (boolean): controls dialog visibility
 * - onClose (function): closes the dialog
 * - organizationName (string): displayed in the dialog's text
 * - templateScope (string): "organization" or "global"
 * - orgId (string): the user's organization ID (if "organization" scope)
 * - currentUser (object): user info (if you need to record who published)
 */
export default function SubmitTemplateDialog({
  open,
  onClose,
  onPublishSuccess = () => {},
  organizationName = 'My Organization',
  templateScope = 'organization',  // or 'global'
  orgId: orgIdProp = '',
  currentUser: currentUserProp = null
}) {
  // Track whether a form is selected (e.g. a file name or ID)
  const [selectedForm, setSelectedForm] = useState(null);

  // Track the toggle for "Submit a copy"
  const [submitCopy, setSubmitCopy] = useState(false);

  // If toggled on, user can input a custom title
  const [copyTitle, setCopyTitle] = useState('');

  // For category selection
  const [category, setCategory] = useState('');

  // We show a second dialog for picking a form
  const [showPicker, setShowPicker] = useState(false);

  // ------------------------------------------------------------------
  // Fallback-to-context (covers the case where parent doesn’t pass them)
  // ------------------------------------------------------------------
  const { currentUser: ctxUser, organizationId: ctxOrgId } = useAuth();
  const { initializeNewFormLocally }              = useFormContext();

  const currentUser   = currentUserProp ?? ctxUser;
  const organizationId = orgIdProp       || ctxOrgId;

  // ------------------------------------------------------
  // Handlers for picking a form
  // ------------------------------------------------------

  // Handler: user clicks "Select a form" => open the file picker
  const handleSelectForm = () => {
    console.log('[SubmitTemplateDialog] Select a form clicked');
    setShowPicker(true);
  };

  // Handler: user closes the file picker
  const handlePickerClose = () => {
    console.log('[SubmitTemplateDialog] Picker closed without selection');
    setShowPicker(false);
  };

  // Handler: user selects a doc from the file picker
  const handlePickerSelect = (docObj) => {
    console.log('[SubmitTemplateDialog] Form picked:', docObj);
    setSelectedForm(docObj);
    setShowPicker(false);
  };

  // Handler: user clicks the X icon to clear the form
 // const handleClearForm = (e) => {
 //   e.stopPropagation(); // prevent button click from also firing
 //   console.log('[SubmitTemplateDialog] Clearing selected form');
 //   setSelectedForm(null);
 // };

  // ------------------------------------------------------
  // Handlers for toggles & text fields
  // ------------------------------------------------------

  // Handler: toggle switch (submit a copy)
  const handleToggleCopy = (event) => {
    setSubmitCopy(event.target.checked);
  };

  // Handler: text field for copyTitle
  const handleChangeTitle = (event) => {
    setCopyTitle(event.target.value);
  };

  // Handler: category
  const handleChangeCategory = (event) => {
    setCategory(event.target.value);
  };

  // ------------------------------------------------------
  // Main "Submit" handler for publishing a template
  // ------------------------------------------------------

  const handleSubmit = async () => {
    console.log('[SubmitTemplateDialog] Submit clicked');
    console.log('Selected Form:', selectedForm);
    console.log('Submit a copy:', submitCopy);
    console.log('Copy title:', copyTitle);
    console.log('Category:', category);
    console.log('Template scope:', templateScope);

    // 1) Ensure a form is selected
    if (!selectedForm) {
      console.warn('[SubmitTemplateDialog] No form selected; cannot publish template.');
      onClose();
      return;
    }

    try {
      // 2) Call our publishTemplate function
      //    Pass all necessary info for Firestore
      const result = await publishTemplate({
        formId: selectedForm.id,   // doc ID from the picked form
        submitCopy,
        copyTitle,
        category,
        templateScope,            // 'organization' or 'global'
        orgId: organizationId,
        currentUser,
        initializeNewFormLocally           
      });

      if (result.status === 'success') {
        console.log('[SubmitTemplateDialog] Template published successfully:', result);
         // Call optimistic‑refresh callback so parent can refetch
        onPublishSuccess({ scope: templateScope, formId: result.formId });
      } else {
        console.error('[SubmitTemplateDialog] Publish error:', result.message);
      }
    } catch (err) {
      console.error('[SubmitTemplateDialog] Unexpected error during publish:', err);
    }

    // 3) Close dialog afterwards
    onClose();
  };

  // Handler: user clicks "Cancel"
  const handleCancel = () => {
    console.log('[SubmitTemplateDialog] Cancel clicked');
    onClose();
  };

  // "Submit" is disabled until a form is selected
  const isSubmitDisabled = !selectedForm;

  // ------------------------------------------------------
  // Render the component
  // ------------------------------------------------------

  return (
    <>
      {/* Primary dialog for "Submit Template" */}
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="sm"
      >
        {/* Dialog header */}
        <DialogTitle sx={{ color: 'text.primary' }}>
          Submit a template
        </DialogTitle>

        {/* Dialog content (no "dividers") */}
        <DialogContent sx={{ color: 'text.secondary' }}>
          {/* Description referencing org name */}
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          {templateScope === 'organization'
            ? `Select a file to add to ${organizationName}’s template gallery. Anyone in your organization will be able to find and use it.`
            : 'Select a file to add to the app‑wide template gallery. All users will be able to find and use it.'}
        </Typography>
          {/* Button to select or show selected form */}
          <Box sx={{ mb: 2 }}>
            {selectedForm ? (
              // If a form is selected, show the form name inside the button
<Button
  variant="outlined"
  onClick={handleSelectForm}
  sx={{ 
    textTransform: 'none',
    minWidth: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }}
>
  {selectedForm.title || 'No title'}

  {/* Replace IconButton with a Box to avoid button-in-button */}
  <Box
    sx={{ ml: 1, display: 'flex', cursor: 'pointer' }}
    onClick={(e) => {
      e.stopPropagation();  // prevent the parent Button’s onClick
      console.log('[SubmitTemplateDialog] Clearing selected form from icon');
      setSelectedForm(null);
    }}
  >
    <CloseIcon sx={{ fontSize: '0.7rem' }} />
  </Box>
</Button>

            ) : (
              // Otherwise, show "Select a form"
              <Button
                variant="outlined"
                onClick={handleSelectForm}
                sx={{ 
                  textTransform: 'none',
                  minWidth: 160 
                }}
              >
                Select a form
              </Button>
            )}
          </Box>

          {/* Toggle switch for "submit a copy" */}
          <FormControlLabel
            control={
              <Switch
                checked={submitCopy}
                onChange={handleToggleCopy}
              />
            }
            label={
              <Typography 
                variant="body2" 
                sx={{ fontSize: '0.85rem', color: 'text.secondary' }}
              >
                Submit a copy of this file instead of the original
              </Typography>
            }
          />

          {/* If toggled on, show text field for new title (standard variant) */}
          {submitCopy && (
            <TextField
              label="Title for the copy"
              variant="standard"
              fullWidth
              sx={{ mt: 2 }}
              value={copyTitle}
              onChange={handleChangeTitle}
            />
          )}

          {/* Category selector with 2 items (standard variant) */}
          <FormControl 
            variant="standard" 
            fullWidth 
            sx={{ mt: 3 }}
          >
            <InputLabel id="template-category-label">Category</InputLabel>
            <Select
              labelId="template-category-label"
              value={category}
              label="Category"
              onChange={handleChangeCategory}
              sx={{ fontSize: '0.85rem' }}  // smaller text in the selector
            >
              <MenuItem 
                value="Campaign URL Builder" 
                sx={{ fontSize: '0.85rem' }}
              >
                Campaign URL Builder
              </MenuItem>
              <MenuItem 
                value="Marketing Request Form" 
                sx={{ fontSize: '0.85rem' }}
              >
                Marketing Request Form
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        {/* Dialog actions with "Submit" and "Cancel" */}
        <DialogActions>
          <Button 
            onClick={handleCancel} 
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ textTransform: 'none' }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Secondary dialog: "OpenFormPickerDialog" for picking a doc */}
      <OpenFormPickerDialog
        open={showPicker}
        onClose={handlePickerClose}
        onSelectForm={handlePickerSelect}
      />
    </>
  );
}
